import { paginationEnum } from "@seims/shared";
import { Grid } from "antd";
import { useMemo } from "react";
const { useBreakpoint } = Grid;

/**
 * @typedef {Object} ResponsiveSettingsOverrides
 * @property {number} [xxlPageSize] - Page size for ultra-wide monitors (xxl)
 * @property {number} [mdXlPageSize] - Page size for tablets/laptops (md, lg, xl)
 * @property {number} [xsSmPageSize] - Page size for mobile devices (xs, sm)
 * @property {number} [xxlItemSize] - Page item size for ultra-wide monitors (xxl)
 * @property {number} [mdXlItemSize] - Page item size for tablets/laptops (md, lg, xl)
 * @property {number} [xsSmItemSize] - Page item size for mobile devices (xs, sm)
 */

/**
 * A hook to handle responsive layouts and dynamic pagination.
 * 
 * @param {ResponsiveSettingsOverrides} [overrides={}] - Optional page size overrides
 * @returns {{ screens: object, pageSize: number, pageItemSizeSM: number, isMobile: boolean }}
 */
export const useResponsiveSettings = (overrides = {}) => {
  const screens = useBreakpoint();

  const pageSize = useMemo(() => {
    const {
      xxlPageSize = paginationEnum.DEFAULT_XLL_PAGE_SIZE,
      mdXlPageSize = paginationEnum.DEFAULT_MD_XL_PAGE_SIZE,
      xsSmPageSize = paginationEnum.DEFAULT_XS_SM_PAGE_SIZE,
      smMdPageSize = paginationEnum.DEFAULT_SM_MD_PAGE_SIZE,
    } = overrides;

    if (screens.xxl) return xxlPageSize; // 8 or more
    if (screens.xl) return mdXlPageSize; // 6
    if (screens.md) return smMdPageSize; // 4
    if (screens.xs || screens.sm) return xsSmPageSize; // 3

    return xxlPageSize;
  }, [screens, overrides]);

  const pageItemSizeSM = useMemo(() => {
    const {
      xxlItemSize = paginationEnum.DEFAULT_MD_XL_ITEM_SIZE_SM,
      mdXlItemSize = paginationEnum.DEFAULT_ITEM_SIZE_SM,
      smItemSize = paginationEnum.DEFAULT_SM_ITEM_SIZE_SM,
      xsSmItemSize = paginationEnum.DEFAULT_XS_ITEM_SIZE_SM,
    } = overrides;

    if (screens.xs) return smItemSize;
    if (screens.sm) return xsSmItemSize;
    if (screens.xxl) return xxlItemSize;


    return mdXlItemSize;
  }, [screens, overrides]);

  const isMobile = useMemo(() => {
    return (screens.xs || screens.sm) && !screens.md;
  }, [screens]);

  return {
    screens,
    pageSize,
    pageItemSizeSM,
    isMobile,
  };
};

export default useResponsiveSettings;
